/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter } from "@i18n/next";
import { useAnalytics } from "@packages/analytics";
import type { TheWordGame, TriviaGame } from "@packages/sdk";
import { getURLFromDeeplink, useSession } from "@packages/sdk";
import {
  CloseIcon,
  END_OF_BROWSER_ASYNC_QUEUE,
  IconButton,
  Loader,
  shades,
  spacing,
  useTheme,
  zIndices,
} from "@packages/ui/emotion";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";

import { GameFrame } from "./GameFrame";

const closeStyle: Interpolation = {
  height: spacing.xxl,
  position: "fixed",
  right: spacing.xxl,
  top: spacing.xxl,
  width: spacing.xxl,
  zIndex: zIndices.closeOverGames,
};
const overlayStyle: Interpolation = {
  backgroundColor: shades.shade40,
  display: "flex",
  inset: spacing.none,
  placeContent: "center",
  placeItems: "center",
  position: "fixed",
  zIndex: zIndices.modalOverlay,
};

type HallowGame = TriviaGame | TheWordGame;

type GameHandlerProps<T extends HallowGame> = {
  gameSrc: string;
  onShare: (game: T) => Promise<void>;
  usesBootable?: boolean;
  needsClose?: boolean;
};

const SECONDS_UNTIL_WE_GIVE_UP = 10;

export const GameHandler = <T extends HallowGame>({
  usesBootable,
  needsClose,
  onShare,
  gameSrc,
}: GameHandlerProps<T>) => {
  //#region Contexts & Major Hooks
  const analytics = useAnalytics();
  const router = useRouter();
  const { token, refresh } = useSession();
  //#endregion
  //#region States
  const [loading, setLoading] = useState<boolean>(true);
  const [pageIsReady, setPageIsReady] = useState<boolean>(false);
  //#endregion
  //#region Refs
  const iframe = useRef<HTMLIFrameElement>(null);
  // we need the timer and attempts trackers because iframe loading is inconsistent; if the browser page is already
  // loaded (via cache, for example), the iframe doesn't fire an onLoad event; further, if there's a problem with
  // loading a trivia game, the trivia app doesn't send an error, it just... doesn't do anything. So we need to handle
  // those two situations.
  const loadTimer = useRef<NodeJS.Timeout | null>(null);
  const loadAttempts = useRef<number>(0);
  const { themeContainerRef } = useTheme();
  //#endregion
  //#region Effects
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.top.addEventListener("message", handleMessage);
    }

    // when the page the frame points to is already loaded (via cache or some other means), the frame
    // doesn't fire the onLoad event, which means we need a backup way of triggering the game. This way,
    // if we DO get the onLoad event, it will clear this timer and restart the boot attempt
    loadTimer.current = setTimeout(sendBootMessage, END_OF_BROWSER_ASYNC_QUEUE);

    setPageIsReady(!!themeContainerRef.current);

    return () => {
      if (typeof window !== "undefined") {
        window.top.removeEventListener("message", handleMessage);
      }

      setPageIsReady(false);
    };
  }, []);
  //#endregion

  //#region Event handlers
  const close = () => {
    setLoading(true);
    router.push("/");
  };

  //#region Message handlers
  const handleTrackMessage = ({ event, properties }) =>
    analytics.track({
      event,
      properties,
    });

  const handleLoaded = () => {
    clearTimeout(loadTimer.current);
    setLoading(false);
  };

  const handleRefresh = useCallback(
    async (forceNewToken: boolean = false) => {
      let usedToken = token;
      if (!usedToken || forceNewToken) {
        const data = await refresh();
        usedToken = data.access_token;
      }

      sendMessage({
        access_token: usedToken,
        version: parseInt(process.env.DOPPLER_PUBLIC_X_HALLOW_VERSION),
      });
    },
    [token, refresh],
  );

  const handleNavigate = ({ link }) => {
    const path = getURLFromDeeplink({ deeplink: link });
    if (path) router.push(path);
  };
  //#endregion

  const handleMessage = async (msg) => {
    if (msg?.origin === gameSrc && msg.data) {
      const { event, properties } = msg.data;
      switch (event) {
        case "track":
          return handleTrackMessage(properties);
        case "bootable":
          return sendBootMessage();
        case "loaded":
          return handleLoaded();
        case "didTapClose":
          return close();
        case "didTapShare":
          return onShare(properties);
        case "authExpired":
          return handleRefresh(true);
        case "deeplink":
          return handleNavigate(properties);
      }
    }
  };
  //#endregion

  //#region Helper functions
  const sendMessage = (msg) => {
    if (iframe.current) iframe.current.contentWindow.postMessage(msg, gameSrc);
  };

  const handleSlowLoad = () => {
    if (loadAttempts.current++ > SECONDS_UNTIL_WE_GIVE_UP) {
      close();
      return;
    }

    return sendBootMessage();
  };

  const sendBootMessage = async () => {
    await handleRefresh();
    clearTimeout(loadTimer.current);
    loadTimer.current = setTimeout(handleSlowLoad, 1000);
  };
  //#endregion

  if (!pageIsReady) return <Loader />;

  return createPortal(
    <RemoveScroll enabled={true}>
      <GameFrame
        src={gameSrc}
        onLoad={usesBootable ? null : sendBootMessage}
        ref={iframe}
        key={"triviaFrameComponent"}
      />
      {needsClose && (
        <IconButton
          icon={<CloseIcon />}
          variant={"transparentWhite"}
          onClick={close}
          css={closeStyle}
        />
      )}
      {loading ? <div css={overlayStyle} /> : null}
    </RemoveScroll>,
    themeContainerRef.current,
  );
};
